/**
 * Org-defined extra fields on the Employee Details page — "the columns should be customizable,
 * company can add any columns they want." A field an admin defines here shows up as an input on
 * Add/Edit Employee and the Employee Details page, AND as a toggleable column on the Employee
 * List (apps/web/src/features/employees/columns.tsx merges these in alongside the fixed columns).
 *
 * Same ADR 0022 catalogue shape as Grade/CostCentre/ExitType (org.settings:read/update), but the
 * catalogue here is field DEFINITIONS, not values — one employee's VALUES travel on
 * employee.contract.ts's `employeeSchema.customFields` / create/update bodies instead, keyed by
 * `key`, because they are as much a property of the employee record as `designation` is.
 *
 * Admin CRUD (this file) is `org.settings:update`-gated. Reading which fields exist — the shape,
 * not any employee's values — needs a lower bar: anyone who can see the employee list/detail at
 * all needs to know what columns/inputs to render, so that read is `listActiveEmployeeCustomFields`
 * on lookup.contract.ts, gated by `employee:read` like every other picker there.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, orgIdSchema, uuidSchema } from '../schemas/common.js';
import { BUILT_IN_EMPLOYEE_FIELD_SECTIONS } from '../lib/built-in-employee-fields.js';
import {
  CUSTOM_FIELD_EDIT_APPROVAL,
  CUSTOM_FIELD_MANAGER_ACCESS,
  CUSTOM_FIELD_SELF_ACCESS,
} from '../lib/custom-field-access.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'dropdown'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];
export const customFieldTypeSchema = z.enum(CUSTOM_FIELD_TYPES);

export const employeeCustomFieldDefinitionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  key: z.string(),
  label: z.string(),
  type: customFieldTypeSchema,
  /** DROPDOWN's choices, in display order; null for every other type. */
  options: z.array(z.string()).nullable(),
  isRequired: z.boolean(),
  isActive: z.boolean(),
  /** Which profile section the field renders in — a built-in section key
   *  (`BUILT_IN_EMPLOYEE_FIELD_SECTIONS`: personal / work / bank) or the key of a section the org
   *  added (employee-field-sections.contract.ts). */
  sectionKey: z.string(),
  /** Shown under the input on Add/Edit Employee; null when the admin left it blank. */
  helpText: z.string().nullable(),
  /** Keka's field permissions — see packages/shared/src/lib/custom-field-access.ts. HR always
   *  sees and edits; these say what the employee and their reporting manager may do. */
  selfAccess: z.enum(CUSTOM_FIELD_SELF_ACCESS),
  /** Only meaningful when `selfAccess` is `edit`. */
  selfEditApproval: z.enum(CUSTOM_FIELD_EDIT_APPROVAL),
  managerAccess: z.enum(CUSTOM_FIELD_MANAGER_ACCESS),
  /**
   * May connected applications receive this field (`{{employee.custom.<key>}}`, the standard
   * payload's `customFields`)? Off by default: a custom field can hold anything (ADR 0033, G14).
   * Optional until the column exists (application integrations PR 2); absent means false.
   */
  shareWithApplications: z.boolean().optional(),
  /** Position within its section (ascending). */
  sortOrder: z.number().int(),
});
export type EmployeeCustomFieldDefinition = z.infer<typeof employeeCustomFieldDefinitionSchema>;

/**
 * What the List/Detail/Add-Edit surfaces actually need — the admin-only bookkeeping fields
 * (orgId, isActive) dropped, since this is what `listActiveEmployeeCustomFields` (lookup.contract.ts)
 * returns and that route only ever returns active ones.
 */
export const employeeCustomFieldOptionSchema = employeeCustomFieldDefinitionSchema.omit({
  orgId: true,
  isActive: true,
});
export type EmployeeCustomFieldOption = z.infer<typeof employeeCustomFieldOptionSchema>;

/**
 * One employee's custom field values, on the wire: `key` (EmployeeCustomFieldDefinition.key) to a
 * raw string, or `null` to clear it. Every type crosses the wire as a string — a NUMBER value as a
 * decimal string (CLAUDE.md invariant 2's convention, `decimalStringSchema`), a DATE value as
 * YYYY-MM-DD, DROPDOWN as one of its configured options — because which of those a given key means
 * is only known by looking up that org's own field definitions, not from this schema alone. The API
 * layer parses and validates each value against its definition's `type`/`options` at write time
 * (apps/api/src/routes/employee.ts) — this schema only bounds the shape.
 */
export const customFieldValueMapSchema = z.record(z.string(), z.string().max(500).nullable());
export type CustomFieldValueMap = z.infer<typeof customFieldValueMapSchema>;

/** Machine key shared by custom fields and custom sections. */
export const employeeFieldKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, numbers and underscores, starting with a letter');

const optionsSchema = z.array(z.string().trim().min(1).max(200)).min(1).max(50);

/** Same shape as a field key. The API checks it names a section that exists (422 otherwise). */
const sectionKeySchema = employeeFieldKeySchema;

const helpTextSchema = z.string().trim().max(300);

export const createEmployeeCustomFieldBodySchema = z
  .object({
    key: employeeFieldKeySchema,
    label: z.string().trim().min(1).max(200),
    type: customFieldTypeSchema,
    options: optionsSchema.optional(),
    isRequired: z.boolean().optional(),
    /** Defaults to `personal`. The new field goes last in its section. */
    sectionKey: sectionKeySchema.optional(),
    helpText: helpTextSchema.optional(),
    /** Default HR-only: `hidden` / `hr` / `hidden`. */
    selfAccess: z.enum(CUSTOM_FIELD_SELF_ACCESS).optional(),
    selfEditApproval: z.enum(CUSTOM_FIELD_EDIT_APPROVAL).optional(),
    managerAccess: z.enum(CUSTOM_FIELD_MANAGER_ACCESS).optional(),
    /** Default false. See the definition schema. */
    shareWithApplications: z.boolean().optional(),
  })
  .refine((value) => value.type !== 'dropdown' || (value.options && value.options.length > 0), {
    message: 'A dropdown field needs at least one option',
    path: ['options'],
  });

/** The key and type are immutable once created — existing values are stored against both. */
export const updateEmployeeCustomFieldBodySchema = z.object({
  label: z.string().trim().min(1).max(200).optional(),
  options: optionsSchema.optional(),
  isRequired: z.boolean().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  /** Moving to another section puts the field last there, unless `sortOrder` is also sent. */
  sectionKey: sectionKeySchema.optional(),
  /** `null` clears it. */
  helpText: helpTextSchema.nullable().optional(),
  selfAccess: z.enum(CUSTOM_FIELD_SELF_ACCESS).optional(),
  selfEditApproval: z.enum(CUSTOM_FIELD_EDIT_APPROVAL).optional(),
  managerAccess: z.enum(CUSTOM_FIELD_MANAGER_ACCESS).optional(),
  /** Turning it on or off marks people out of date in every application; it sends nothing (N5). */
  shareWithApplications: z.boolean().optional(),
});

/**
 * The new order of one section's fields, top to bottom. `fieldIds` must list every field in that
 * section (active or not) exactly once — a partial list is a 422, so two admins reordering at once
 * can't leave the section half-sorted.
 */
export const reorderEmployeeCustomFieldsBodySchema = z.object({
  sectionKey: sectionKeySchema,
  fieldIds: z.array(uuidSchema).min(1).max(500),
});

const statusQuerySchema = z.object({
  status: z.enum(['active', 'inactive', 'all']).default('all'),
});

const deleteResultSchema = z.object({ success: z.boolean() });

const readMeta = routeMeta('org.settings:read');
const writeMeta = routeMeta('org.settings:update');

/**
 * One BUILT-IN employee field (Personal/Work/Bank Information), the registry entry
 * (`packages/shared/src/lib/built-in-employee-fields.ts`) merged with this org's own override, if
 * any (`EmployeeFieldPolicy`). `isRequired`/`requiresApproval` are the EFFECTIVE values — override
 * when one exists, else the registry default (`locked` fields are always `isRequired: true`;
 * non-`selfService` fields always report `requiresApproval: null`, since there is no self-service
 * edit path for them to gate).
 */
export const builtInEmployeeFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  section: z.enum(BUILT_IN_EMPLOYEE_FIELD_SECTIONS),
  locked: z.boolean(),
  selfService: z.boolean(),
  isRequired: z.boolean(),
  requiresApproval: z.boolean().nullable(),
});
export type BuiltInEmployeeField = z.infer<typeof builtInEmployeeFieldSchema>;

export const updateEmployeeFieldPolicyBodySchema = z.object({
  isRequired: z.boolean().optional(),
  requiresApproval: z.boolean().optional(),
});

export const employeeCustomFieldsContract = c.router(
  {
    listEmployeeCustomFields: {
      method: 'GET',
      path: '/employee-custom-fields',
      query: statusQuerySchema,
      responses: {
        200: z.object({ items: z.array(employeeCustomFieldDefinitionSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List custom fields defined on the employee list/detail, for the admin table',
      metadata: readMeta,
    },
    createEmployeeCustomField: {
      method: 'POST',
      path: '/employee-custom-fields',
      body: createEmployeeCustomFieldBodySchema,
      responses: {
        201: employeeCustomFieldDefinitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Add a custom field to a section of the employee profile',
      metadata: writeMeta,
    },
    // Declared before `/:fieldId` routes; the method differs anyway (PUT vs PATCH/DELETE).
    reorderEmployeeCustomFields: {
      method: 'PUT',
      path: '/employee-custom-fields/order',
      body: reorderEmployeeCustomFieldsBodySchema,
      responses: {
        200: z.object({ items: z.array(employeeCustomFieldDefinitionSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Set the order of the custom fields within one profile section',
      metadata: writeMeta,
    },
    updateEmployeeCustomField: {
      method: 'PATCH',
      path: '/employee-custom-fields/:fieldId',
      body: updateEmployeeCustomFieldBodySchema,
      responses: {
        200: employeeCustomFieldDefinitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Rename, move to another section, reorder, or (de)activate a custom field',
      metadata: writeMeta,
    },
    // Unlike Grade/CostCentre/ExitType, a custom field's values are current facts on the employee
    // record (like a phone number), not effective-dated history worth preserving — so delete is not
    // refused once used. Every employee's stored value for it is deleted along with it (the FK
    // cascades — see EmployeeCustomFieldValue in schema.prisma).
    deleteEmployeeCustomField: {
      method: 'DELETE',
      path: '/employee-custom-fields/:fieldId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Delete a custom field and every employee value stored against it',
      metadata: writeMeta,
    },

    // --- Built-in field policy (Personal/Work/Bank Information) --------------------------------
    listBuiltInEmployeeFields: {
      method: 'GET',
      path: '/employee-fields',
      responses: {
        200: z.object({ items: z.array(builtInEmployeeFieldSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'The built-in employee fields (Personal/Work/Bank Information), with this org’s Required / self-service-approval overrides',
      metadata: readMeta,
    },
    updateEmployeeFieldPolicy: {
      method: 'PATCH',
      path: '/employee-fields/:fieldKey',
      body: updateEmployeeFieldPolicyBodySchema,
      responses: {
        200: builtInEmployeeFieldSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Override whether a built-in field is required on Add Employee, or needs HR approval to self-edit',
      metadata: writeMeta,
    },
  },
  { pathPrefix: '/organisation' },
);
